import { useState, useEffect, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Package, Truck, Box, ArrowRight, CheckCircle, Clock, Settings,
  Warehouse, CreditCard, BarChart3, ShoppingCart, FileText, Plus,
  RefreshCw, ChevronRight, DollarSign, Layers, PackageCheck
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const FF_STATUS = {
  processing: { label: "Procesando", color: "bg-blue-100 text-blue-700", icon: RefreshCw },
  picking: { label: "Picking", color: "bg-yellow-100 text-yellow-700", icon: Package },
  packing: { label: "Empacando", color: "bg-orange-100 text-orange-700", icon: Box },
  shipped: { label: "Enviado", color: "bg-purple-100 text-purple-700", icon: Truck },
  delivered: { label: "Entregado", color: "bg-green-100 text-green-700", icon: CheckCircle },
  cancelled: { label: "Cancelado", color: "bg-red-100 text-red-700", icon: Clock },
};

const FulfillmentDashboard = () => {
  const { user, authAxios, loading: authLoading } = useAuth();
  const [pipeline, setPipeline] = useState(null);
  const [orders, setOrders] = useState([]);
  const [billing, setBilling] = useState(null);
  const [settings, setSettings] = useState(null);
  const [storage, setStorage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showNewOrder, setShowNewOrder] = useState(false);
  const [products, setProducts] = useState([]);
  const [settingsForm, setSettingsForm] = useState({ fulfillment_type: "hybrid", auto_select_carrier: true, carrier_priority: "cost", auto_generate_guide: true, default_packaging: null, include_insurance: false, webhook_url: "" });
  const [orderForm, setOrderForm] = useState({ recipient_name: "", recipient_phone: "", destination_cp: "", origin_cp: "06600", notes: "", priority: "cost", items: [] });
  const [filterStatus, setFilterStatus] = useState("all");

  const fetchData = useCallback(async () => {
    try {
      const [plRes, ordRes, bilRes, setRes, stRes, prRes] = await Promise.all([
        authAxios.get("/orders/pipeline/summary"),
        authAxios.get("/orders"),
        authAxios.get("/billing/summary"),
        authAxios.get("/fulfillment/settings"),
        authAxios.get("/warehouse/storage"),
        authAxios.get("/inventory/products")
      ]);
      setPipeline(plRes.data); setOrders(ordRes.data); setBilling(bilRes.data);
      setSettings(setRes.data); setStorage(stRes.data); setProducts(prRes.data);
      setSettingsForm({ ...setRes.data, webhook_url: setRes.data.webhook_url || "" });
    } catch (e) { /* error handled */; } finally { setLoading(false); }
  }, [authAxios]);

  useEffect(() => { if (user) fetchData(); }, [user, fetchData]);

  const handleSaveSettings = async () => {
    try {
      await authAxios.put("/fulfillment/settings", settingsForm);
      toast.success("Configuración guardada");
      setShowSettings(false);
      fetchData();
    } catch (e) { toast.error("Error al guardar"); }
  };

  const handleCreateOrder = async (e) => {
    e.preventDefault();
    if (orderForm.items.length === 0) { toast.error("Agrega al menos un producto"); return; }
    try {
      const response = await authAxios.post("/orders/create", {
        ...orderForm,
        source: "manual",
        items: orderForm.items.map(i => ({ product_id: i.product_id, quantity: parseInt(i.quantity) }))
      });
      toast.success(`Pedido ${response.data.order_number} creado${response.data.tracking_number ? ` - Guía: ${response.data.tracking_number}` : ''}`);
      setShowNewOrder(false);
      setOrderForm({ recipient_name: "", recipient_phone: "", destination_cp: "", origin_cp: "06600", notes: "", priority: "cost", items: [] });
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || "Error al crear pedido"); }
  };

  const addItem = () => setOrderForm(prev => ({ ...prev, items: [...prev.items, { product_id: "", quantity: "1" }] }));
  const removeItem = (i) => setOrderForm(prev => ({ ...prev, items: prev.items.filter((_, idx) => idx !== i) }));
  const updateItem = (i, field, value) => setOrderForm(prev => ({ ...prev, items: prev.items.map((item, idx) => idx === i ? { ...item, [field]: value } : item) }));

  const filteredOrders = filterStatus === "all" ? orders : orders.filter(o => o.fulfillment_status === filterStatus);

  if (authLoading) return null;
  if (!user) return <Navigate to="/login" />;

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#FAF9F6]" data-testid="fulfillment-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#5D4037]">Centro de Fulfillment</h1>
            <p className="text-stone-600">Gestión automatizada de pedidos, inventario y envíos</p>
          </div>
          <div className="flex gap-3">
            <button onClick={() => setShowSettings(true)} className="btn-secondary flex items-center gap-2 text-sm py-2" data-testid="ff-settings-btn"><Settings size={16} /> Configurar</button>
            <button onClick={() => setShowNewOrder(true)} className="btn-primary flex items-center gap-2 text-sm py-2" data-testid="ff-new-order-btn"><Plus size={16} /> Nuevo Pedido</button>
          </div>
        </div>

        {/* Pipeline */}
        {pipeline && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-8">
            {Object.entries(FF_STATUS).map(([key, cfg]) => {
              const Icon = cfg.icon;
              return (
                <motion.div key={key} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className={`bento-card p-4 cursor-pointer ${filterStatus === key ? "ring-2 ring-[#F57C00]" : ""}`} onClick={() => setFilterStatus(filterStatus === key ? "all" : key)} data-testid={`pipeline-${key}`}>
                  <div className="flex items-center gap-2 mb-2"><Icon size={16} className="text-stone-500" /></div>
                  <p className="text-2xl font-black text-[#5D4037]">{pipeline[key] || 0}</p>
                  <p className="text-xs text-stone-500">{cfg.label}</p>
                </motion.div>
              );
            })}
            <div className="bento-card p-4 bg-[#2E7D32] text-white">
              <DollarSign size={16} className="mb-2 text-white/70" />
              <p className="text-2xl font-black">${pipeline.total_fulfillment_revenue?.toLocaleString("es-MX")}</p>
              <p className="text-xs text-white/70">Ingresos FF</p>
            </div>
          </div>
        )}

        {/* Billing + Storage Quick Stats */}
        {billing && (
          <div className="grid md:grid-cols-3 gap-4 mb-8">
            <div className="bento-card p-5 flex items-center gap-4">
              <div className="w-12 h-12 bg-[#F57C00]/10 rounded-xl flex items-center justify-center"><Warehouse size={24} className="text-[#F57C00]" /></div>
              <div>
                <p className="text-sm text-stone-500">Almacenamiento</p>
                <p className="font-bold text-[#5D4037]">{billing.storage.units} uds / {billing.storage.volume_m3} m³</p>
                <p className="text-xs text-stone-400">${billing.storage.monthly_cost}/mes</p>
              </div>
            </div>
            <div className="bento-card p-5 flex items-center gap-4">
              <div className="w-12 h-12 bg-[#2E7D32]/10 rounded-xl flex items-center justify-center"><PackageCheck size={24} className="text-[#2E7D32]" /></div>
              <div>
                <p className="text-sm text-stone-500">Fulfillment</p>
                <p className="font-bold text-[#5D4037]">{billing.fulfillment.orders_count} pedidos</p>
                <p className="text-xs text-stone-400">${billing.fulfillment.total_cost.toLocaleString("es-MX")} MXN</p>
              </div>
            </div>
            <div className="bento-card p-5 flex items-center gap-4">
              <div className="w-12 h-12 bg-[#5D4037]/10 rounded-xl flex items-center justify-center"><CreditCard size={24} className="text-[#5D4037]" /></div>
              <div>
                <p className="text-sm text-stone-500">Total Cargos</p>
                <p className="font-bold text-[#5D4037]">${billing.total_charges.toLocaleString("es-MX")} MXN</p>
                <p className="text-xs text-stone-400">Este período</p>
              </div>
            </div>
          </div>
        )}

        {/* Orders List */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-[#5D4037]">Pedidos ({filteredOrders.length})</h2>
          {filterStatus !== "all" && <button onClick={() => setFilterStatus("all")} className="text-sm text-[#F57C00] hover:underline">Ver todos</button>}
        </div>

        {loading ? <div className="text-center py-12">Cargando...</div> : filteredOrders.length === 0 ? (
          <div className="bento-card p-12 text-center">
            <ShoppingCart size={48} className="text-stone-300 mx-auto mb-4" />
            <p className="text-stone-500 font-medium">{orders.length === 0 ? "Sin pedidos aún" : "Sin pedidos con este filtro"}</p>
            <p className="text-sm text-stone-400 mt-2">Crea un pedido manual o conecta un marketplace para automatizar</p>
            <div className="flex justify-center gap-3 mt-6">
              <button onClick={() => setShowNewOrder(true)} className="btn-primary text-sm py-2">Crear Pedido</button>
              <Link to="/multicanal" className="btn-secondary text-sm py-2">Conectar Marketplace</Link>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredOrders.map((order) => {
              const statusCfg = FF_STATUS[order.fulfillment_status] || FF_STATUS.processing;
              return (
                <motion.div key={order.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bento-card p-5" data-testid={`order-${order.id}`}>
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-mono font-bold text-[#5D4037]">{order.order_number}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusCfg.color}`}>{statusCfg.label}</span>
                        <span className="text-xs text-stone-400">{order.source}</span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-stone-600">
                        <span>{order.recipient?.name}</span>
                        <span>CP {order.recipient?.cp}</span>
                        <span>{order.total_items} items / {order.total_weight}kg</span>
                      </div>
                      {order.tracking_number && (
                        <div className="flex items-center gap-2 mt-2">
                          <Truck size={14} className="text-[#2E7D32]" />
                          <span className="text-sm font-mono text-[#2E7D32]">{order.tracking_number}</span>
                          <span className="text-xs text-stone-400">{order.shipping?.carrier} - {order.shipping?.service}</span>
                        </div>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="font-bold text-[#5D4037]">${order.product_value?.toLocaleString("es-MX")}</p>
                      <p className="text-xs text-stone-400">FF: ${order.total_fulfillment_cost?.toLocaleString("es-MX")}</p>
                      <p className="text-xs text-stone-400 mt-1">{new Date(order.created_at).toLocaleDateString("es-MX")}</p>
                    </div>
                  </div>
                  {/* Timeline */}
                  {order.timeline && (
                    <div className="mt-3 pt-3 border-t border-stone-100 flex items-center gap-2 overflow-x-auto">
                      {order.timeline.map((t, i) => (
                        <div key={i} className="flex items-center gap-1 flex-shrink-0">
                          <div className={`w-2 h-2 rounded-full ${i === order.timeline.length - 1 ? "bg-[#2E7D32]" : "bg-stone-300"}`} />
                          <span className="text-xs text-stone-500">{t.description}</span>
                          {i < order.timeline.length - 1 && <ChevronRight size={12} className="text-stone-300" />}
                        </div>
                      ))}
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Settings Modal */}
        {showSettings && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 w-full max-w-lg mx-4 max-h-[85vh] overflow-y-auto">
              <h3 className="text-lg font-bold text-[#5D4037] mb-4">Configuración de Fulfillment</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-semibold text-[#5D4037]">Modelo de Fulfillment</label>
                  <select value={settingsForm.fulfillment_type} onChange={(e) => setSettingsForm({...settingsForm, fulfillment_type: e.target.value})} className="w-full h-10 px-3 rounded-lg border border-stone-200 outline-none bg-white text-sm mt-1" data-testid="ff-type-select">
                    <option value="tdc_warehouse">Almacén TDC (Full Fulfillment)</option>
                    <option value="self_ship">Envío propio (Solo guías)</option>
                    <option value="hybrid">Híbrido (Ambos)</option>
                  </select>
                </div>
                <div className="flex items-center justify-between"><span className="text-sm">Selección automática de paquetería</span><input type="checkbox" checked={settingsForm.auto_select_carrier} onChange={(e) => setSettingsForm({...settingsForm, auto_select_carrier: e.target.checked})} className="w-5 h-5" /></div>
                <div>
                  <label className="text-sm font-semibold text-[#5D4037]">Prioridad de envío</label>
                  <select value={settingsForm.carrier_priority} onChange={(e) => setSettingsForm({...settingsForm, carrier_priority: e.target.value})} className="w-full h-10 px-3 rounded-lg border border-stone-200 outline-none bg-white text-sm mt-1">
                    <option value="cost">Menor costo</option>
                    <option value="speed">Mayor velocidad</option>
                  </select>
                </div>
                <div className="flex items-center justify-between"><span className="text-sm">Generar guía automáticamente</span><input type="checkbox" checked={settingsForm.auto_generate_guide} onChange={(e) => setSettingsForm({...settingsForm, auto_generate_guide: e.target.checked})} className="w-5 h-5" /></div>
                <div className="flex items-center justify-between"><span className="text-sm">Incluir seguro</span><input type="checkbox" checked={settingsForm.include_insurance} onChange={(e) => setSettingsForm({...settingsForm, include_insurance: e.target.checked})} className="w-5 h-5" /></div>
                <div>
                  <label className="text-sm font-semibold text-[#5D4037]">Webhook URL (notificaciones)</label>
                  <input type="url" value={settingsForm.webhook_url} onChange={(e) => setSettingsForm({...settingsForm, webhook_url: e.target.value})} placeholder="https://tu-servidor.com/webhook" className="w-full h-10 px-3 rounded-lg border border-stone-200 outline-none text-sm mt-1" />
                </div>
                <div className="flex gap-3 pt-2">
                  <button onClick={() => setShowSettings(false)} className="flex-1 h-10 border border-stone-200 rounded-lg font-medium text-sm">Cancelar</button>
                  <button onClick={handleSaveSettings} className="flex-1 btn-primary h-10 text-sm" data-testid="save-ff-settings">Guardar</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* New Order Modal */}
        {showNewOrder && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 w-full max-w-lg mx-4 max-h-[85vh] overflow-y-auto">
              <h3 className="text-lg font-bold text-[#5D4037] mb-4">Nuevo Pedido</h3>
              <form onSubmit={handleCreateOrder} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs font-semibold text-[#5D4037]">Destinatario *</label><input type="text" value={orderForm.recipient_name} onChange={(e) => setOrderForm({...orderForm, recipient_name: e.target.value})} className="w-full h-10 px-3 rounded-lg border border-stone-200 outline-none text-sm" required data-testid="order-recipient" /></div>
                  <div><label className="text-xs font-semibold text-[#5D4037]">Teléfono</label><input type="tel" value={orderForm.recipient_phone} onChange={(e) => setOrderForm({...orderForm, recipient_phone: e.target.value})} className="w-full h-10 px-3 rounded-lg border border-stone-200 outline-none text-sm" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs font-semibold text-[#5D4037]">CP Origen</label><input type="text" value={orderForm.origin_cp} onChange={(e) => setOrderForm({...orderForm, origin_cp: e.target.value})} maxLength={5} className="w-full h-10 px-3 rounded-lg border border-stone-200 outline-none text-sm font-mono" /></div>
                  <div><label className="text-xs font-semibold text-[#5D4037]">CP Destino *</label><input type="text" value={orderForm.destination_cp} onChange={(e) => setOrderForm({...orderForm, destination_cp: e.target.value})} maxLength={5} className="w-full h-10 px-3 rounded-lg border border-stone-200 outline-none text-sm font-mono" required data-testid="order-dest-cp" /></div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-semibold text-[#5D4037]">Productos</label>
                    <button type="button" onClick={addItem} className="text-xs text-[#F57C00] font-medium hover:underline" data-testid="add-item-btn">+ Agregar</button>
                  </div>
                  {orderForm.items.map((item, i) => (
                    <div key={i} className="flex gap-2 mb-2">
                      <select value={item.product_id} onChange={(e) => updateItem(i, "product_id", e.target.value)} className="flex-1 h-9 px-2 rounded-lg border border-stone-200 text-sm outline-none bg-white" data-testid={`item-select-${i}`}>
                        <option value="">Seleccionar...</option>
                        {products.map(p => <option key={p.id} value={p.id}>{p.name} ({p.sku}) - ${p.price} - Stock: {p.stock}</option>)}
                      </select>
                      <input type="number" value={item.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)} min="1" className="w-16 h-9 px-2 rounded-lg border border-stone-200 text-sm outline-none text-center" />
                      <button type="button" onClick={() => removeItem(i)} className="text-red-400 hover:text-red-600 text-sm px-2">x</button>
                    </div>
                  ))}
                  {orderForm.items.length === 0 && <p className="text-xs text-stone-400 text-center py-3">Agrega productos al pedido</p>}
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setShowNewOrder(false)} className="flex-1 h-10 border border-stone-200 rounded-lg font-medium text-sm">Cancelar</button>
                  <button type="submit" className="flex-1 btn-primary h-10 text-sm" data-testid="create-order-btn">Crear Pedido</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FulfillmentDashboard;
